import { ChevronDown, ChevronUp } from "lucide-react"
import { useLocalization } from "../../lib/i18n"

type HomeActionPromoBarProps = {
  onJoinPool?: () => void
  onCreatePool?: () => void
  canJoinPool?: boolean
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

export function HomeActionPromoBar({
  onJoinPool,
  onCreatePool,
  canJoinPool = true,
  collapsed = false,
  onCollapsedChange,
}: HomeActionPromoBarProps) {
  const { t } = useLocalization()
  const toggleLabel = collapsed ? t("home.promo.soccer.expand") : t("home.promo.soccer.minimize")
  const ToggleIcon = collapsed ? ChevronDown : ChevronUp
  const actions = (
    <div className="grid shrink-0 grid-cols-[1fr_1fr_auto] gap-2 sm:flex sm:items-center">
      <button
        type="button"
        onClick={onJoinPool}
        disabled={!canJoinPool || !onJoinPool}
        className="inline-flex min-h-9 items-center justify-center rounded-lg border border-white/30 bg-black/50 px-4 py-2 font-display text-sm font-bold uppercase text-white transition hover:border-brand/70 hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t("home.promo.soccer.join")}
      </button>
      <button
        type="button"
        onClick={onCreatePool}
        disabled={!onCreatePool}
        className="inline-flex min-h-9 items-center justify-center rounded-lg border border-brand bg-brand px-4 py-2 font-display text-sm font-bold uppercase text-black transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t("home.promo.soccer.create")}
      </button>
      <button
        type="button"
        onClick={() => onCollapsedChange?.(!collapsed)}
        className="inline-flex min-h-9 w-10 items-center justify-center rounded-lg border border-white/18 bg-black/35 text-white transition hover:border-brand/70 hover:text-brand"
        aria-label={toggleLabel}
      >
        <ToggleIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )

  return (
    <section
      className={`relative overflow-hidden rounded-lg border transition-[min-height,border-color,background-color] duration-300 ease-out ${
        collapsed ? "min-h-[48px] border-transparent bg-transparent" : "min-h-[64px] border-brand/35 bg-background-card"
      }`}
    >
      <img
        src="/img/soccer-splash2.jpg"
        alt=""
        aria-hidden="true"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-out ${
          collapsed ? "opacity-0" : "opacity-100"
        }`}
      />
      <div
        className={`absolute inset-0 transition-opacity duration-300 ease-out ${
          collapsed
            ? "opacity-0"
            : "bg-[linear-gradient(90deg,rgba(8,10,14,0.94)_0%,rgba(8,10,14,0.86)_58%,rgba(8,10,14,0.44)_100%)] opacity-100"
        }`}
      />
      <div
        className={`relative z-10 flex flex-col gap-3 px-4 transition-[min-height,padding] duration-300 ease-out sm:flex-row sm:items-center sm:justify-between sm:gap-5 ${
          collapsed ? "min-h-[48px] py-1.5" : "min-h-[64px] py-3"
        }`}
      >
        {!collapsed ? (
          <div className="min-w-0 animate-[promo-copy-enter_220ms_ease-out]">
            <p className="font-display text-[11px] font-bold uppercase leading-none tracking-[0.16em] text-white/62">
              {t("home.promo.soccer.context")}
            </p>
            <div className="mt-1 flex min-w-0 flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
              <h2 className="min-w-0 break-words font-display text-[21px] font-bold uppercase leading-[1.05] text-brand sm:text-[24px]">
                {t("home.promo.soccer.title")}
              </h2>
              <p className="min-w-0 break-words text-sm font-semibold leading-snug text-white/82">
                {t("home.promo.soccer.body")}
              </p>
            </div>
          </div>
        ) : null}
        {actions}
      </div>
    </section>
  )
}
